import dayjs from "~/config/dayjs";
import searchService from "~/services/searchService";
import { handleError } from "~/utils/handleError";

// Keep candidates on the availability grid, including blocks that started earlier today.
export const computeProfessionalDaySlots = (professional, timeLapse, now = new Date()) => {
	const duration = Number(professional?.duration) * 60000;
	const lapse = Number(timeLapse > 0 ? timeLapse : professional?.duration) * 60000;
	const vacancies = Number(professional?.vacancies);
	if (!(duration > 0) || !(lapse > 0) || !(vacancies > 0) ||
		!Number.isFinite(duration) || !Number.isFinite(lapse)) return [];

	const unavailable = (professional?.unavailability ?? []).map((block) => ({
		start: new Date(block.start).getTime(), end: new Date(block.end).getTime(),
	})).filter((block) => block.start < block.end);
	const slots = [];
	const seen = new Set();
	for (const block of professional?.availability ?? []) {
		const start = new Date(block.start).getTime();
		const final = new Date(block.end).getTime();
		if (!(start < final)) continue;
		let current = start + Math.max(0, Math.ceil((now.getTime() - start) / lapse)) * lapse;
		while (current + duration <= final) {
			const end = current + duration;
			const collisions = unavailable.filter((block) => block.start < end && block.end > current);
			if (collisions.length < vacancies) {
				if (!seen.has(current)) {
					seen.add(current);
					slots.push({ start: new Date(current).toISOString(), end: new Date(end).toISOString() });
				}
				current += lapse;
			} else {
				current = Math.min(...collisions.map((block) => block.end));
			}
		}
	}
	return slots.sort((a, b) => new Date(a.start) - new Date(b.start));
};

/**
 * Busca, para o serviço/profissional/dia de um agendamento expirado por
 * antecedência mínima, os próximos horários ainda disponíveis naquele
 * mesmo dia. Depende de `expiredService.service.featureItems` (categoryIds
 * do serviço) para poder consultar o /search — quando ausente, não há como
 * buscar de forma confiável e a função retorna `hasFeatureItems: false`
 * sem chamar a API.
 *
 * Quando o profissional original não tem agenda e não aparece nos resultados
 * do search, faz fallback agregando slots de todos os profissionais disponíveis
 * para o mesmo serviço naquele dia (cada slot carrega `professionalId`).
 */
export const fetchAlternativeSlotsForService = async ({ business, expiredService, language }) => {
	const featureItems = expiredService?.service?.featureItems;
	if (!featureItems || !business?.id) {
		return { slots: [], hasFeatureItems: false };
	}

	const day = dayjs(expiredService.dateStart || expiredService.date).utc().tz(business?.timeZone);

	try {
		const { data } = await searchService.postSearch({
			start_time: day.startOf("day").utc().format(),
			end_time: day.endOf("day").utc().format(),
			business_id: business.id,
			services: [featureItems],
			language: language || business?.language,
		});

		const serviceEntry = data?.[0]?.services?.find((s) => s?.id === expiredService.service_id);
		const timeLapse = data?.[0]?.business?.timeLapse;
		const professionalEntry = serviceEntry?.professionals?.find((p) => p?.id === expiredService.professional_id);

		const expiredStart = new Date(expiredService.dateStart || expiredService.date).getTime();

		if (!professionalEntry) {
			// Profissional sem agenda: agrega slots de todos os profissionais disponíveis
			const allProfessionals = serviceEntry?.professionals ?? [];
			const now = Date.now();
			const aggregatedSlots = allProfessionals
				.flatMap((prof) =>
					computeProfessionalDaySlots(prof, timeLapse)
						.filter((slot) => new Date(slot.start).getTime() > now)
						.map((slot) => ({ ...slot, professionalId: prof.id }))
				)
				.sort((a, b) => new Date(a.start) - new Date(b.start));

			return { slots: aggregatedSlots, hasFeatureItems: true };
		}

		const allSlots = computeProfessionalDaySlots(professionalEntry, timeLapse);

		const futureSlots = allSlots
			.filter((slot) => new Date(slot.start).getTime() > expiredStart)
			.sort((a, b) => new Date(a.start) - new Date(b.start));

		return { slots: futureSlots, hasFeatureItems: true };
	} catch (err) {
		handleError(err, "Checkout/fetchAlternativeSlotsForService");
		return { slots: [], hasFeatureItems: true };
	}
};
